/**
 * src/components/statement/AppleCardStatementModal.tsx
 * Ultra-Premium Apple Card Style Statement Preview & Multi-Page PDF Exporter
 * Generates official high-definition vector PDF statements via jsPDF + autoTable.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { GlassModal } from '../ui/GlassModal';
import { api, type TransactionItem, type UserItem } from '../../services/api';
import {
  FileDown,
  Download,
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  Layers,
  Calendar,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
  Loader2,
  FileSpreadsheet,
  Wifi,
  Sparkles
} from 'lucide-react';
import { generateExecutiveStatementPdf } from '../../utils/generatePdfStatement';
import { exportLedgerToCsv } from '../../utils/exportStatement';
import { haptic, playTapSound, playSuccessSound } from '../../utils/audioHaptics';
import { useToast } from '../../context/ToastContext';

interface AppleCardStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: TransactionItem[];
  currentUser?: UserItem | null;
  summary: {
    totalReceived: number;
    totalPaid: number;
    netDifference: number;
  };
}

type TimeframeFilter = 'all' | 'this_month' | 'last_30';
type TypeFilter = 'all' | 'receive' | 'paid';

export const AppleCardStatementModal: React.FC<AppleCardStatementModalProps> = ({
  isOpen,
  onClose,
  transactions,
  currentUser,
  summary
}) => {
  const { success, error: toastError } = useToast();

  const [timeframe, setTimeframe] = useState<TimeframeFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const [dataset, setDataset] = useState<TransactionItem[]>(transactions);
  const [isLoadingDataset, setIsLoadingDataset] = useState<boolean>(false);

  // Sync with prop when passed
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      setDataset(transactions);
    }
  }, [transactions]);

  // Fetch complete ledger dataset (all 1500+ records) when statement modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingDataset(true);
      api.transactions.list({ limit: 10000 })
        .then(res => {
          if (res.transactions && res.transactions.length > 0) {
            setDataset(res.transactions);
          }
        })
        .catch(err => {
          console.error('Failed to load full statement dataset:', err);
        })
        .finally(() => {
          setIsLoadingDataset(false);
        });
    }
  }, [isOpen]);

  // Filtered transactions based on modal filters
  const filteredTransactions = useMemo(() => {
    let result = [...dataset];

    // Timeframe filtering
    const now = new Date();
    if (timeframe === 'this_month') {
      const currentYearMonth = now.toISOString().slice(0, 7); // YYYY-MM
      result = result.filter(t => t.transaction_date && t.transaction_date.startsWith(currentYearMonth));
    } else if (timeframe === 'last_30') {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      result = result.filter(t => {
        if (!t.transaction_date) return false;
        const d = new Date(t.transaction_date);
        return d >= thirtyDaysAgo;
      });
    }

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(t => t.transaction_type === typeFilter);
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        t =>
          (t.description && t.description.toLowerCase().includes(q)) ||
          (t.category_name && t.category_name.toLowerCase().includes(q)) ||
          (t.username && t.username.toLowerCase().includes(q))
      );
    }

    // Sort newest first
    return result.sort((a, b) => {
      const dateA = a.transaction_date || '';
      const dateB = b.transaction_date || '';
      return dateB.localeCompare(dateA);
    });
  }, [dataset, timeframe, typeFilter, searchQuery]);

  // Recalculate summary metrics for active filter
  const activeSummary = useMemo(() => {
    const received = filteredTransactions
      .filter(t => t.transaction_type === 'receive')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const paid = filteredTransactions
      .filter(t => t.transaction_type === 'paid')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    return {
      totalReceived: received,
      totalPaid: paid,
      netDifference: received - paid,
      transactionCount: filteredTransactions.length
    };
  }, [filteredTransactions]);

  // Top spending categories for insights
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    filteredTransactions.forEach(t => {
      const name = t.category_name || 'General';
      map[name] = (map[name] || 0) + (t.amount || 0);
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [filteredTransactions]);

  // Period label for export & PDF
  const periodLabel = useMemo(() => {
    if (timeframe === 'this_month') {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (timeframe === 'last_30') {
      return 'Last 30 Days';
    }
    return 'All Recorded Transactions';
  }, [timeframe]);

  // Trigger PDF Generation
  const handleDownloadPdf = async () => {
    haptic.tap();
    playTapSound();
    setIsGeneratingPdf(true);

    try {
      await generateExecutiveStatementPdf({
        transactions: filteredTransactions,
        summary: activeSummary,
        currentUser,
        periodLabel,
        filename: `CKAudit_Statement_${new Date().toISOString().slice(0, 10)}_${(currentUser?.username || 'All').replace(/\s+/g, '_')}.pdf`
      });

      haptic.success();
      playSuccessSound();
      success('Official Executive PDF Statement downloaded!', 'Download Complete');
    } catch (err: any) {
      console.error('PDF Generation Error:', err);
      toastError(err.message || 'Failed to generate PDF document');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Trigger CSV export
  const handleExportCsv = () => {
    haptic.tap();
    playTapSound();
    exportLedgerToCsv(
      filteredTransactions,
      `CKAudit_Statement_${new Date().toISOString().slice(0, 10)}.csv`
    );
    haptic.success();
    playSuccessSound();
    success('Excel-compatible CSV downloaded!', 'CSV Exported');
  };

  const todayStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="CKAudit Titanium Statement"
      maxWidth="860px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        
        {/* 1. Titanium Apple Card Physical Representation */}
        <div
          style={{
            position: 'relative',
            borderRadius: '24px',
            padding: '24px 28px',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #020617 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.2)',
            overflow: 'hidden',
            color: '#ffffff'
          }}
        >
          {/* Subtle titanium laser sheen background */}
          <div
            style={{
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
              pointerEvents: 'none'
            }}
          />

          {/* Top row: Chip & Contactless Wave + Hologram Badge */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Gold Titanium EMV Chip Graphic */}
              <div
                style={{
                  width: '42px',
                  height: '32px',
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 50%, #d97706 100%)',
                  boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.6), 0 2px 4px rgba(0,0,0,0.3)',
                  border: '0.5px solid #b45309',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div style={{ width: '80%', height: '50%', border: '0.5px solid rgba(0,0,0,0.25)', borderRadius: '3px' }} />
              </div>

              {/* NFC Contactless Wave */}
              <Wifi size={18} color="rgba(255, 255, 255, 0.65)" style={{ transform: 'rotate(90deg)' }} />
            </div>

            {/* Titanium Holographic Pill */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(12px)',
                border: '0.5px solid rgba(255, 255, 255, 0.2)',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#e2e8f0'
              }}
            >
              <ShieldCheck size={14} color="#34d399" />
              <span>Certified Audit</span>
            </div>
          </div>

          {/* Card Middle: CKAudit Wordmark & Statement Reference */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', color: '#94a3b8', textTransform: 'uppercase' }}>
                CKAudit Titanium Ledger
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff', marginTop: '4px' }}>
                {currentUser?.username ? currentUser.username.toUpperCase() : 'EXECUTIVE MASTER'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#cbd5e1', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{currentUser?.role === 'admin' ? 'Master Administrator' : 'Ledger Member'}</span>
                <span>•</span>
                <span>ID: #CK-{currentUser?.id || '001'}</span>
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Net Balance
              </div>
              <div
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.02em',
                  color: activeSummary.netDifference >= 0 ? '#4ade80' : '#f87171',
                  marginTop: '2px'
                }}
              >
                {activeSummary.netDifference >= 0 ? '+' : ''}₹{Math.abs(activeSummary.netDifference).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>
                {activeSummary.netDifference >= 0 ? 'Net Surplus Position' : 'Net Debit Obligation'}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Controls & Filter Bar */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            background: 'var(--bg-secondary)',
            padding: '12px 16px',
            borderRadius: '16px',
            border: 'var(--border-hairline)'
          }}
        >
          {/* Timeframe selector tabs */}
          <div style={{ display: 'flex', gap: '6px', background: 'rgba(0, 0, 0, 0.1)', padding: '3px', borderRadius: '12px' }}>
            {(
              [
                { id: 'all', label: 'All Records' },
                { id: 'this_month', label: 'This Month' },
                { id: 'last_30', label: 'Last 30 Days' }
              ] as const
            ).map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  setTimeframe(tab.id);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '9px',
                  border: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: timeframe === tab.id ? 'var(--ios-blue)' : 'transparent',
                  color: timeframe === tab.id ? '#ffffff' : 'var(--text-secondary)',
                  transition: 'all 0.15s ease'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Type filter & search box */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              {(
                [
                  { id: 'all', label: 'All' },
                  { id: 'receive', label: currentUser?.role === 'admin' ? 'Credits (+)' : 'Paid by Me (+)' },
                  { id: 'paid', label: currentUser?.role === 'admin' ? 'Debits (-)' : 'Paid by Them (-)' }
                ] as const
              ).map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    haptic.tap();
                    playTapSound();
                    setTypeFilter(t.id);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: 'var(--border-hairline)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: typeFilter === t.id ? 'var(--bg-tertiary)' : 'transparent',
                    color: typeFilter === t.id ? 'var(--text-primary)' : 'var(--text-tertiary)'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Quick search input */}
            <div style={{ position: 'relative', width: '160px' }}>
              <Search
                size={14}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)'
                }}
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px 6px 30px',
                  borderRadius: '8px',
                  border: 'var(--border-hairline)',
                  background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  fontSize: '0.78rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        </div>

        {/* 3. Executive KPI Metric Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px'
          }}
        >
          {/* Credits Received */}
          <div
            className="ios-inset-group"
            style={{
              margin: 0,
              padding: '16px',
              borderLeft: '4px solid var(--ios-green)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                {currentUser?.role === 'admin' ? 'Total Credits' : 'Total Paid by Me'}
              </span>
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '6px',
                  background: 'rgba(52, 199, 89, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ios-green)'
                }}
              >
                <ArrowDownLeft size={14} />
              </div>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--ios-green)', marginTop: '6px', fontVariantNumeric: 'tabular-nums' }}>
              +₹{activeSummary.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {filteredTransactions.filter(t => t.transaction_type === 'receive').length} credits logged
            </div>
          </div>

          {/* Debits Paid Out */}
          <div
            className="ios-inset-group"
            style={{
              margin: 0,
              padding: '16px',
              borderLeft: '4px solid var(--ios-red)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                {currentUser?.role === 'admin' ? 'Total Debits' : 'Total Paid by Them'}
              </span>
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '6px',
                  background: 'rgba(255, 69, 58, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ios-red)'
                }}
              >
                <ArrowUpRight size={14} />
              </div>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--ios-red)', marginTop: '6px', fontVariantNumeric: 'tabular-nums' }}>
              -₹{activeSummary.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {filteredTransactions.filter(t => t.transaction_type === 'paid').length} debits logged
            </div>
          </div>

          {/* Statement Position */}
          <div
            className="ios-inset-group"
            style={{
              margin: 0,
              padding: '16px',
              borderLeft: `4px solid ${activeSummary.netDifference >= 0 ? 'var(--ios-green)' : 'var(--ios-red)'}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                Net Position
              </span>
              <span
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '999px',
                  background: activeSummary.netDifference >= 0 ? 'rgba(52, 199, 89, 0.15)' : 'rgba(255, 69, 58, 0.15)',
                  color: activeSummary.netDifference >= 0 ? 'var(--ios-green)' : 'var(--ios-red)'
                }}
              >
                {activeSummary.netDifference >= 0 ? 'Surplus' : 'Deficit'}
              </span>
            </div>
            <div
              style={{
                fontSize: '1.25rem',
                fontWeight: 800,
                color: activeSummary.netDifference >= 0 ? 'var(--ios-green)' : 'var(--ios-red)',
                marginTop: '6px',
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {activeSummary.netDifference >= 0 ? '+' : ''}₹{activeSummary.netDifference.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {activeSummary.transactionCount} transactions included
            </div>
          </div>
        </div>

        {/* 4. Category Spending Breakdown Strip */}
        {categoryBreakdown.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              padding: '10px 14px',
              borderRadius: '12px',
              background: 'var(--bg-secondary)',
              border: 'var(--border-hairline)',
              fontSize: '0.75rem'
            }}
          >
            <span style={{ fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Top Flow:
            </span>
            {categoryBreakdown.map(([cat, amt]) => (
              <div
                key={cat}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  background: 'var(--bg-tertiary)',
                  border: 'var(--border-hairline)'
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{cat}</span>
                <span style={{ color: 'var(--ios-blue)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  ₹{amt.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 5. Itemized Transactions Activity List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 4px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)' }}>
              Itemized Statement Activity ({filteredTransactions.length} records)
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
              Period: {periodLabel}
            </span>
          </div>

          <div
            className="ios-inset-group"
            style={{
              margin: 0,
              maxHeight: '340px',
              overflowY: 'auto',
              borderRadius: '16px',
              border: 'var(--border-hairline)'
            }}
          >
            {filteredTransactions.length === 0 ? (
              <div style={{ padding: '36px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                No transactions match your statement filters.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                <thead>
                  <tr
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: 'var(--bg-secondary)',
                      zIndex: 2,
                      borderBottom: 'var(--border-hairline)',
                      color: 'var(--text-tertiary)',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <th style={{ padding: '10px 14px' }}>Date</th>
                    <th style={{ padding: '10px 14px' }}>Description</th>
                    <th style={{ padding: '10px 14px' }}>Category</th>
                    <th style={{ padding: '10px 14px' }}>Member</th>
                    <th style={{ padding: '10px 14px' }}>Type</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t, idx) => {
                    const isCredit = t.transaction_type === 'receive';
                    return (
                      <tr
                        key={t.id || idx}
                        style={{
                          borderBottom: 'var(--border-hairline)',
                          transition: 'background 0.15s ease'
                        }}
                      >
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {t.transaction_date ? t.transaction_date.slice(0, 10) : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {t.description || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              background: 'var(--bg-tertiary)',
                              fontSize: '0.72rem',
                              fontWeight: 500
                            }}
                          >
                            {t.category_name || 'General'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                          {t.username || 'System'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              background: isCredit ? 'rgba(52, 199, 89, 0.12)' : 'rgba(255, 69, 58, 0.12)',
                              color: isCredit ? 'var(--ios-green)' : 'var(--ios-red)'
                            }}
                          >
                            {isCredit ? 'CREDIT' : 'DEBIT'}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '10px 14px',
                            textAlign: 'right',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: isCredit ? 'var(--ios-green)' : 'var(--ios-red)'
                          }}
                        >
                          {isCredit ? '+' : '-'}₹{(t.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 6. High-End Action Footer: PDF Download & CSV Export */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            paddingTop: '12px',
            borderTop: 'var(--border-hairline)'
          }}
        >
          {/* Secondary actions: Close & CSV */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              className="ios-tap-target"
              style={{
                padding: '12px 20px',
                borderRadius: 'var(--radius-pill)',
                border: 'var(--border-hairline)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Close
            </button>

            <button
              type="button"
              onClick={handleExportCsv}
              className="ios-tap-target"
              style={{
                padding: '12px 18px',
                borderRadius: 'var(--radius-pill)',
                border: 'var(--border-hairline)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FileSpreadsheet size={16} color="var(--ios-green)" />
              <span>Export CSV</span>
            </button>
          </div>

          {/* Primary Action: Download Official Vector PDF */}
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf || filteredTransactions.length === 0}
            className="ios-primary-button ios-tap-target"
            style={{
              padding: '12px 24px',
              borderRadius: 'var(--radius-pill)',
              fontSize: '0.94rem',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, var(--ios-blue) 0%, #4338ca 100%)',
              color: '#ffffff',
              boxShadow: '0 8px 20px -6px rgba(79, 70, 229, 0.5)',
              border: 'none',
              cursor: isGeneratingPdf ? 'wait' : 'pointer',
              opacity: filteredTransactions.length === 0 ? 0.5 : 1
            }}
          >
            {isGeneratingPdf ? (
              <>
                <Loader2 size={18} className="spin" />
                <span>Rendering Vector PDF...</span>
              </>
            ) : (
              <>
                <FileDown size={18} />
                <span>Download Official PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </GlassModal>
  );
};
